import { DOCUMENT_KIND_LABELS, ORDER_STATUSES } from '@wms/domain';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../api';
import { fmt } from '../../format';
import { useSession } from '../../session';
import type { DocumentSettings, PendingDocuments } from '../../types';
import { Empty, ErrorBanner, Spinner, StatusChip, useLoad, useToast } from '../../ui';
import { UploadDocumentDialog, type SellerDocumentKind } from '../../ui/documents';
import { Icon } from '../../ui/icons';
import { useSort } from '../../ui/sort';

const KINDS: readonly SellerDocumentKind[] = ['e_waybill', 'e_archive_invoice'];

/** e-Belge yönetimi: which documents travel with the parcel, and the orders still waiting for them. */
export function SellerDocuments({ onChange }: { onChange: () => void }) {
  const { me } = useSession();
  const toast = useToast();
  const settings = useLoad(() => api<DocumentSettings>('GET', '/api/settings/documents'), []);
  const pending = useLoad(() => api<PendingDocuments[]>('GET', '/api/documents/pending'), [], 30_000);
  const [draft, setDraft] = useState<DocumentSettings | null>(null);
  const [secret, setSecret] = useState('');
  const [uploading, setUploading] = useState<{ order: PendingDocuments; kind: SellerDocumentKind } | null>(null);
  const owner = me.role === 'owner';
  const sort = useSort<PendingDocuments>('pending-documents', {
    order: { label: 'Sipariş', value: (o) => o.orderNumber },
    status: { label: 'Durum', value: (o) => ORDER_STATUSES.indexOf(o.status) },
    missing: { label: 'Eksik belge', value: (o) => o.missing.map((k) => DOCUMENT_KIND_LABELS[k]).join(' ') },
    date: { label: 'Sipariş tarihi', value: (o) => o.orderedAt, desc: true },
  });

  useEffect(() => {
    if (settings.data) setDraft(settings.data);
  }, [settings.data]);

  const toggle = (list: readonly SellerDocumentKind[], kind: SellerDocumentKind) => (list.includes(kind) ? list.filter((k) => k !== kind) : [...list, kind]);

  const save = async () => {
    if (!draft) return;
    try {
      const saved = await api<DocumentSettings>('PUT', '/api/settings/documents', {
        provider: draft.provider,
        requiredDocuments: draft.requiredDocuments,
        printedDocuments: draft.printedDocuments,
        bridgeUrl: draft.provider === 'http' ? draft.bridgeUrl : null,
        ...(secret ? { bridgeSecret: secret } : {}),
      });
      setDraft(saved);
      setSecret('');
      toast('Belge ayarları kaydedildi', 'ok');
      void settings.reload();
      void pending.reload();
      onChange();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  const waits = (settings.data?.requiredDocuments.length ?? 0) > 0;
  return (
    <div className="split">
      <section className="card">
        <div className="card-h">
          <Icon name="doc" />
          <h2>Belge bekleyen siparişler</h2>
          <span className="sub">{waits ? 'Paketleme bu belgeler olmadan tamamlanmaz' : 'Depo belge beklemiyor'}</span>
        </div>
        {pending.error && <ErrorBanner message={pending.error} onRetry={() => void pending.reload()} />}
        {!pending.data ? (
          <Spinner />
        ) : pending.data.length === 0 ? (
          <Empty title="Bekleyen belge yok">
            {waits ? 'Zorunlu belgeleri tüm açık siparişlere yüklediniz.' : 'Siparişleriniz belge beklemeden paketlenip kargoya verilir; faturanızı entegratörünüz keser.'}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  {sort.th('order')}
                  {sort.th('status')}
                  {sort.th('missing')}
                  {sort.th('date')}
                  <th />
                </tr>
              </thead>
              <tbody>
                {sort.apply(pending.data).map((o) => (
                  <tr key={o.orderId}>
                    <td className="mono">{o.orderNumber}</td>
                    <td>
                      <StatusChip status={o.status} />
                    </td>
                    <td>
                      {o.missing.map((k) => (
                        <span key={k} className="chip warn" style={{ marginRight: 4 }}>
                          {DOCUMENT_KIND_LABELS[k]}
                        </span>
                      ))}
                    </td>
                    <td className="nowrap">{fmt.dateTime(o.orderedAt)}</td>
                    <td className="num">
                      {o.missing.map((k) => (
                        <button key={k} className="btn sm" disabled={me.role === 'viewer'} onClick={() => setUploading({ order: o, kind: k })}>
                          <Icon name="upload" size={16} /> {DOCUMENT_KIND_LABELS[k]} yükle
                        </button>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <div className="card-h">
          <h2>Belge ayarları</h2>
          {!owner && <span className="sub">Yalnızca hesap sahibi değiştirebilir</span>}
        </div>
        {!draft ? (
          <Spinner />
        ) : (
          <div className="card-b stack">
            <p className="muted" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>
              Faturanızı entegratörünüz ya da pazaryeri kesiyorsa (e-Arşiv, “irsaliye yerine geçer”) aşağıda hiçbir belgeyi işaretlemeyin: depo belge
              beklemeden gönderir. Bir belgeyi zorunlu yaparsanız o belge yüklenmeden sipariş paketlenmez ve kargoya verilmez.
            </p>
            <div className="field">
              <label>Belgeler nasıl gelir?</label>
              <div className="row">
                <button className={`chip select${draft.provider === 'seller_upload' ? ' on' : ''}`} disabled={!owner} onClick={() => setDraft({ ...draft, provider: 'seller_upload' })}>
                  Panelden yüklerim
                </button>
                <button className={`chip select${draft.provider === 'http' ? ' on' : ''}`} disabled={!owner} onClick={() => setDraft({ ...draft, provider: 'http' })}>
                  Entegratörüm gönderir
                </button>
              </div>
            </div>
            {draft.provider === 'http' && (
              <>
                <div className="field">
                  <label htmlFor="bridge-url">Entegratör köprü adresi (https)</label>
                  <input id="bridge-url" className="input mono" value={draft.bridgeUrl ?? ''} disabled={!owner} onChange={(e) => setDraft({ ...draft, bridgeUrl: e.target.value })} />
                </div>
                <div className="field">
                  <label htmlFor="bridge-secret">İmza anahtarı {draft.bridgeSecretSet && <span className="muted">(kayıtlı; değiştirmek için yazın)</span>}</label>
                  <input id="bridge-secret" className="input" type="password" autoComplete="new-password" value={secret} disabled={!owner} onChange={(e) => setSecret(e.target.value)} />
                </div>
              </>
            )}
            <div className="field">
              <label>Paketlemeden önce zorunlu</label>
              {KINDS.map((k) => (
                <label key={k} className="row" style={{ cursor: owner ? 'pointer' : 'default', fontWeight: 500 }}>
                  <input type="checkbox" checked={draft.requiredDocuments.includes(k)} disabled={!owner} onChange={() => setDraft({ ...draft, requiredDocuments: toggle(draft.requiredDocuments, k) })} />
                  {DOCUMENT_KIND_LABELS[k]}
                </label>
              ))}
            </div>
            <div className="field">
              <label>Koliye basılsın</label>
              {KINDS.map((k) => (
                <label key={k} className="row" style={{ cursor: owner ? 'pointer' : 'default', fontWeight: 500 }}>
                  <input type="checkbox" checked={draft.printedDocuments.includes(k)} disabled={!owner} onChange={() => setDraft({ ...draft, printedDocuments: toggle(draft.printedDocuments, k) })} />
                  {DOCUMENT_KIND_LABELS[k]}
                </label>
              ))}
            </div>
            {owner && (
              <button className="btn primary" onClick={() => void save()}>
                Ayarları kaydet
              </button>
            )}
          </div>
        )}
      </section>

      {uploading && (
        <UploadDocumentDialog
          orderId={uploading.order.orderId}
          orderNumber={uploading.order.orderNumber}
          kind={uploading.kind}
          onClose={() => setUploading(null)}
          onDone={() => {
            toast(`${uploading.order.orderNumber}: ${DOCUMENT_KIND_LABELS[uploading.kind]} yüklendi`, 'ok');
            setUploading(null);
            void pending.reload();
            onChange();
          }}
        />
      )}
    </div>
  );
}
